import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Calendar, ChevronDown, UserPlus, ThumbsUp, ThumbsDown, Trash2, Pencil } from "lucide-react";
import QualityBadge from "./QualityBadge";
import PreceptorCard from "./PreceptorCard";
import PreceptorRatingForm from "./PreceptorRatingForm";
import ReviewComments from "./ReviewComments";
import EditReviewDialog from "./EditReviewDialog";
import ReportReviewDialog from "./ReportReviewDialog";
import ShareReviewButton from "./ShareReviewButton";
import ExperienceCompletion from "./ExperienceCompletion";
import RatingForm from "./RatingForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { getReviewTypeConfig, type Review } from "@/lib/mockData";
import { CensoredText } from "@/lib/profanityFilter";



interface ReviewCardProps {
  review: Review;
  index: number;
  isAdmin?: boolean;
  onDelete?: (id: string) => void;
  defaultCommentsOpen?: boolean;
  highlightCommentId?: string | null;
}

const DifficultyBar = ({ difficulty }: { difficulty: number }) => {
  const pct = (difficulty / 5) * 100;
  const fillColor =
    difficulty >= 3.6 ? "bg-red-600" : difficulty >= 2.5 ? "bg-yellow-500" : "bg-green-600";
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Difficulty</span>
      <div className="flex-1 h-1.5 rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all ${fillColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-semibold text-foreground">{difficulty.toFixed(1)}</span>
    </div>
  );
};

const useReviewVotes = (reviewId: string) => {
  const [counts, setCounts] = useState({ up: 0, down: 0 });
  const [userVote, setUserVote] = useState<"up" | "down" | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  const fetchVotes = async () => {
    const { data } = await supabase
      .from("review_votes_public" as any)
      .select("vote_type, is_owner")
      .eq("review_id", reviewId);
    if (data) {
      const rows = data as any[];
      setCounts({
        up: rows.filter((v) => v.vote_type === "up").length,
        down: rows.filter((v) => v.vote_type === "down").length,
      });
      const own = rows.find((v) => v.is_owner);
      setUserVote(own ? (own.vote_type as "up" | "down") : null);
    }
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  useEffect(() => {
    fetchVotes();
  }, [reviewId, userId]);

  const handleVote = async (dir: "up" | "down") => {
    if (!userId) {
      toast({ title: "Please sign in to vote", variant: "destructive" });
      return;
    }

    if (userVote === dir) {
      // RLS restricts deletes to the current user's own vote
      await supabase.from("review_votes").delete().eq("review_id", reviewId);
      setCounts((c) => ({ ...c, [dir]: c[dir] - 1 }));
      setUserVote(null);
    } else if (userVote) {
      await supabase
        .from("review_votes")
        .update({ vote_type: dir } as any)
        .eq("review_id", reviewId);
      setCounts((c) => ({
        up: dir === "up" ? c.up + 1 : c.up - 1,
        down: dir === "down" ? c.down + 1 : c.down - 1,
      }));
      setUserVote(dir);
    } else {
      await supabase
        .from("review_votes")
        .insert({ review_id: reviewId, user_id: userId, vote_type: dir } as any);
      setCounts((c) => ({ ...c, [dir]: c[dir] + 1 }));
      setUserVote(dir);
    }
  };

  return { counts, userVote, handleVote };
};

const ReviewCard = ({ review: reviewProp, index, isAdmin, onDelete, defaultCommentsOpen, highlightCommentId }: ReviewCardProps) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [textExpanded, setTextExpanded] = useState(false);
  const [preceptorFormOpen, setPreceptorFormOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [completionKey, setCompletionKey] = useState(0);
  const [patch, setPatch] = useState<Partial<Review>>({});

  const review = { ...reviewProp, ...patch };
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const { counts: votes, userVote: voted, handleVote } = useReviewVotes(review.id);
  const typeConfig = getReviewTypeConfig(review.type);
  const hasPreceptors = typeConfig.hasPreceptors && review.preceptors && review.preceptors.length > 0;


  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setCurrentUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const openPreceptorForm = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      toast({ title: "Sign in required", description: "Please log in or sign up to rate a preceptor." });
      return;
    }
    setPreceptorFormOpen(true);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: index * 0.06 }}
        className="group rounded-xl border bg-card p-5 shadow-card transition-all duration-300 hover:shadow-card-hover hover:-translate-y-0.5 border-border w-full min-w-0 max-w-full overflow-hidden"
      >


        {/* Top row: quality badge + info */}
        <div className="flex gap-4 mb-3">
          <QualityBadge rating={review.rating} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2 flex-wrap">
              <h3 className="font-serif text-lg font-medium text-card-foreground break-words">
                {review.major && review.major.trim() ? review.major : review.title}
              </h3>
            </div>

            <button

              onClick={() => navigate(`/search?q=${encodeURIComponent(review.organization)}`)}
              className="text-sm font-medium text-primary hover:underline cursor-pointer text-left"
            >
              {review.organization}
            </button>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1.5 flex-wrap">
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {review.location}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(review.date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
              </span>
              {(review.startDate || review.endDate) && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {review.startDate
                    ? new Date(review.startDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })
                    : "?"}
                  {" – "}
                  {review.endDate
                    ? new Date(review.endDate).toLocaleDateString("en-US", { month: "short", year: "numeric" })
                    : "Present"}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Difficulty bar + would do again */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <DifficultyBar difficulty={review.difficulty} />
          </div>
          <Badge
            variant={review.wouldDoAgain ? "default" : "secondary"}
            className="text-[10px] whitespace-nowrap rounded-full self-start sm:self-auto"
          >
            {review.wouldDoAgain ? "👍 Would do again" : "👎 Would not do again"}
          </Badge>
        </div>

        <p
          onClick={() => setTextExpanded(!textExpanded)}
          className={`text-sm text-muted-foreground leading-relaxed mb-1 cursor-pointer hover:text-foreground transition-colors ${textExpanded ? "" : "line-clamp-3"}`}
        >
          <CensoredText text={review.review} />
        </p>
        <button
          onClick={() => setTextExpanded(!textExpanded)}
          className="text-xs text-primary font-medium hover:underline mb-3"
        >
          {textExpanded ? "Show less" : "Read more"}
        </button>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {review.tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs font-medium rounded-full px-2.5">
              {tag}
            </Badge>
          ))}
        </div>

        {/* Extra documented details */}
        {(() => {
          const details = ([
            ...(typeConfig.showCompensation ? [["Compensation", review.compensation] as [string, string | null | undefined]] : []),
            ["Interview", review.interviewInfo],
            ["Responsibilities", review.responsibilities],
          ] as Array<[string, string | null | undefined]>).filter(([, v]) => !!v?.trim());

          if (details.length === 0) return null;

          return (
            <div className="space-y-1.5 mb-3">
              {details.map(([label, v]) => (
                <p key={label} className="text-xs text-muted-foreground leading-relaxed">
                  <span className="font-semibold text-foreground">{label}: </span>
                  <CensoredText text={v as string} />
                </p>
              ))}
            </div>
          );
        })()}

        <ExperienceCompletion
          key={completionKey}
          organization={review.organization}
          major={review.major}
          experienceKey={review.experienceKey}
          type={review.type}
          onHelp={() => (review.isOwner ? setEditOpen(true) : setHelpOpen(true))}
          className="mb-3"
        />



        {/* Helpfulness voting */}
        <div className="flex items-center gap-4 pt-3 border-t border-border">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Helpful?</span>
          <button
            onClick={() => handleVote("up")}
            className={`flex items-center gap-1 text-xs transition-colors ${
              voted === "up" ? "text-primary font-semibold" : "text-muted-foreground hover:text-primary"
            }`}
          >
            <ThumbsUp className="w-3.5 h-3.5" />
            {votes.up}
          </button>
          <button
            onClick={() => handleVote("down")}
            className={`flex items-center gap-1 text-xs transition-colors ${
              voted === "down" ? "text-destructive font-semibold" : "text-muted-foreground hover:text-destructive"
            }`}
          >
            <ThumbsDown className="w-3.5 h-3.5" />
            {votes.down}
          </button>
          <ShareReviewButton review={review} />
          <ReportReviewDialog reviewId={review.id} reviewTitle={review.title} />
          {review.isOwner && (
            <button
              onClick={() => setEditOpen(true)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
          )}
          {isAdmin && onDelete && (

            <button
              onClick={() => onDelete(review.id)}
              className="flex items-center gap-1 text-xs text-destructive hover:text-destructive/80 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          )}
        </div>

        {/* Preceptors */}
        {hasPreceptors && (
          <>
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline transition-colors mt-3"
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
              {expanded ? "Hide" : "Show"} Preceptors ({review.preceptors!.length})
            </button>

            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 space-y-2">
                    {review.preceptors!.map((preceptor, i) => (
                      <PreceptorCard key={preceptor.id} preceptor={preceptor} index={i} />
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 gap-1.5 text-xs rounded-lg"
                      onClick={openPreceptorForm}
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      Rate a Preceptor
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {typeConfig.hasPreceptors && !hasPreceptors && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs mt-3 rounded-lg"
            onClick={openPreceptorForm}
          >
            <UserPlus className="w-3.5 h-3.5" />
            Rate a Preceptor
          </Button>
        )}

        <ReviewComments
          reviewId={review.id}
          isReviewOwner={!!review.isOwner}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          defaultOpen={defaultCommentsOpen}
          highlightCommentId={highlightCommentId}
        />
      </motion.div>

      {typeConfig.hasPreceptors && (
        <PreceptorRatingForm
          rotationTitle={review.title}
          open={preceptorFormOpen}
          onClose={() => setPreceptorFormOpen(false)}
        />
      )}

      {editOpen && (
        <EditReviewDialog
          review={review}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={(p) => {
            setPatch((prev) => ({ ...prev, ...p }));
            setCompletionKey((k) => k + 1);
          }}
        />
      )}

      {helpOpen && (
        <RatingForm
          type={review.type}
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          onSubmitted={() => setCompletionKey((k) => k + 1)}
          initialOrganization={review.organization}
          initialCategory={review.major}
        />
      )}

    </>
  );
};

export default ReviewCard;
